/**
 * Centralized query key factory for React Query - V2 endpoints only
 * Query keys use API path keys which come from the centralized apiPaths configuration
 */

import { apiPathKeys } from './api-paths'

// Type for workflow runs list filters (used in query keys)
export type ListWorkflowRunsFilters = {
  page?: number
  page_size?: number
  workflow_type?: string | null
  status?: string[] | null
  trigger_type?: string[] | null
  product_id?: string[] | null
  created_by_user_id?: string[] | null
  created_after?: string | null
  created_before?: string | null
  sort_by?: string | null
  sort_order?: 'asc' | 'desc' | null
}

const queryKeys = {
  auth: {
    all: ['auth'] as const,
    health: () => [apiPathKeys.auth.health()] as const,
    session: () => [apiPathKeys.auth.session()] as const,
    me: () => [apiPathKeys.auth.me()] as const,
    profileImage: (userId: string) => [apiPathKeys.auth.profileImage(userId)] as const,
    userMe: () => [apiPathKeys.auth.userMe()] as const,
  },
  workflows: {
    all: ['workflows'] as const,
    catalog: {
      list: () => [apiPathKeys.workflows.catalog.base()] as const,
      listWithFilters: (filters?: {
        search?: string | null
        category?: string | null
        lifecycle_stage?: string | null
        owner?: string | null
        is_emergence_built?: boolean | null
        org_id?: string | null
      }) => [apiPathKeys.workflows.catalog.base(), filters] as const,
    },
    byWorkflowSlug: {
      metadata: (workflowSlug: string) =>
        [apiPathKeys.workflows.byWorkflowSlug.metadata(workflowSlug)] as const,
      versions: (workflowSlug: string) =>
        [apiPathKeys.workflows.byWorkflowSlug.versions(workflowSlug)] as const,
      stats: (workflowSlug: string, filters?: Record<string, string | undefined>) =>
        [apiPathKeys.workflows.byWorkflowSlug.stats(workflowSlug), filters] as const,
    },
    workflowTypes: {
      all: () => [...queryKeys.workflows.all, 'workflow-types'] as const,
      list: () => [apiPathKeys.workflows.workflowTypes.base()] as const,
      byType: (workflowType: string) =>
        [apiPathKeys.workflows.workflowTypes.byType(workflowType)] as const,
    },
    workflowRuns: {
      all: () => [...queryKeys.workflows.all, 'workflow-runs'] as const,
      /** Prefix for invalidating all list queries regardless of filters */
      listAll: () => [apiPathKeys.workflows.workflowRuns.base()] as const,
      list: (filters?: ListWorkflowRunsFilters) =>
        [apiPathKeys.workflows.workflowRuns.base(), filters] as const,
      byId: (runId: string) => [apiPathKeys.workflows.workflowRuns.byId(runId)] as const,
      byRunNumber: (runNumber: number | string) =>
        [apiPathKeys.workflows.workflowRuns.byRunNumber(runNumber)] as const,
      updates: () => [apiPathKeys.workflows.workflowRuns.updates()] as const,
      logs: (runId: string) => [apiPathKeys.workflows.workflowRuns.logs(runId)] as const,
      logsByRunNumber: (runNumber: number | string) =>
        [apiPathKeys.workflows.workflowRuns.logsByRunNumber(runNumber)] as const,
      logUpdates: (runId: string) =>
        [apiPathKeys.workflows.workflowRuns.logUpdates(runId)] as const,
      cancel: (runId: string) => [apiPathKeys.workflows.workflowRuns.cancel(runId)] as const,
      retry: (runId: string) => [apiPathKeys.workflows.workflowRuns.retry(runId)] as const,
      stats: (orgId: string, period?: string) => [apiPathKeys.workflows.workflowRuns.stats(), orgId, period] as const,
      report: (runId: string) => [apiPathKeys.workflows.workflowRuns.report(runId)] as const,
      reportByRunNumber: (runNumber: number | string) =>
        [apiPathKeys.workflows.workflowRuns.reportByRunNumber(runNumber)] as const,
    },
  },
  reports: {
    all: ['reports'] as const,
    comments: (reportId: string) => [...queryKeys.reports.all, 'comments', reportId] as const,
    exportsList: (params?: {
      report_id?: string | null
      active_only?: boolean
      limit?: number
      offset?: number
    }) => [...queryKeys.reports.all, 'exports', params ?? {}] as const,
  },
  products: {
    all: ['products'] as const,
    list: (q?: string | null) => [apiPathKeys.products.base(), q] as const,
    groups: {
      all: () => [...queryKeys.products.all, 'groups'] as const,
      list: () => [apiPathKeys.products.groups()] as const,
    },
    lots: (productId: string, startDate: string, endDate: string) =>
      [apiPathKeys.products.lots(productId), startDate, endDate] as const,
    lotsMetrics: (productId: string, startDate: string, endDate: string) =>
      [apiPathKeys.products.lotsMetrics(productId), startDate, endDate] as const,
  },
  api: {
    dataSources: {
      list: () => [apiPathKeys.dataSources.base()] as const,
    },
    dataSourceConnections: {
      all: () => [apiPathKeys.dataSourceConnections.base()] as const,
      list: (params?: { is_active?: boolean }) =>
        [apiPathKeys.dataSourceConnections.base(), params ?? {}] as const,
      byId: (connectionId: string) =>
        [apiPathKeys.dataSourceConnections.byId(connectionId)] as const,
    },
    improvements: {
      list: () => [apiPathKeys.improvements.base()] as const,
    },
    productConfigurations: {
      list: () => [apiPathKeys.productConfigurations.base()] as const,
    },
    productConfiguration: {
      detail: (id: string) => [apiPathKeys.productConfiguration.byId(id)] as const,
    },
    userProducts: {
      list: () => [apiPathKeys.userProducts.base()] as const,
    },
    policies: {
      byProduct: (productId: string) => [apiPathKeys.policies.byProduct(productId)] as const,
    },
    excursions: {
      listAll: () => [apiPathKeys.excursions.base()] as const,
      list: (params?: {
        page?: number
        page_size?: number
        sort_by?: string | null
        sort_order?: 'asc' | 'desc' | null
        workflow_run_id?: string | null
        severity?: string[] | null
        excursion_status?: string[] | null
        assignee_id?: string[] | null
        data_source?: string[] | null
        created_after?: string | null
        created_before?: string | null
        query?: string | null
      }) => [apiPathKeys.excursions.base(), params ?? {}] as const,
      metrics: (params: { created_after?: string | null; created_before?: string | null } = {}) =>
        [apiPathKeys.excursions.base(), 'metrics', params] as const,
      metricsAll: () => [apiPathKeys.excursions.base(), 'metrics'] as const,
    },
  },
} as const

export { queryKeys }
